import React, { useEffect, useMemo, useState } from "react";
import "./AdminDashboard.css";
import { adminApi } from "../../utils/adminApi";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const formatMonth = (date) =>
  date.toLocaleString("en-US", { month: "short", year: "2-digit" });

const escapePdfText = (value = "") =>
  String(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

const createSimplePdf = (lines) => {
  const safeLines = lines.map((line) => escapePdfText(line));
  const textCommands = safeLines
    .map((line, index) => `BT /F1 11 Tf 50 ${770 - index * 16} Td (${line}) Tj ET`)
    .join("\n");

  const content = `%PDF-1.4
1 0 obj
<< /Type /Catalog /Pages 2 0 R >>
endobj
2 0 obj
<< /Type /Pages /Kids [3 0 R] /Count 1 >>
endobj
3 0 obj
<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>
endobj
4 0 obj
<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>
endobj
5 0 obj
<< /Length ${textCommands.length} >>
stream
${textCommands}
endstream
endobj
xref
0 6
0000000000 65535 f 
0000000010 00000 n 
0000000061 00000 n 
0000000118 00000 n 
0000000244 00000 n 
0000000314 00000 n 
trailer
<< /Root 1 0 R /Size 6 >>
startxref
${384 + textCommands.length}
%%EOF`;

  return new Blob([content], { type: "application/pdf" });
};

const AdminDashboard = () => {
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [brandsRes, modelsRes, bookingsRes] = await Promise.all([
          adminApi.get("/api/brands"),
          adminApi.get("/api/models"),
          adminApi.get("/api/bookings"),
        ]);

        setBrands(Array.isArray(brandsRes.data) ? brandsRes.data : []);
        setModels(Array.isArray(modelsRes.data) ? modelsRes.data : []);
        setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      } catch (error) {
        console.error("Dashboard fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const stats = useMemo(() => {
    const totalOrders = bookings.length;
    const totalCustomers = new Set(bookings.map((b) => (b.phone || "").trim())).size;
    const pendingRepairs = bookings.filter((b) => (b.status || "Pending") === "Pending").length;
    const completedRepairs = bookings.filter((b) => b.status === "Completed").length;

    return {
      totalOrders,
      totalCustomers,
      totalModels: models.length,
      pendingRepairs,
      completedRepairs,
    };
  }, [bookings, models]);

  const chartData = useMemo(() => {
    const now = new Date();
    const monthBuckets = [];

    for (let i = 5; i >= 0; i -= 1) {
      monthBuckets.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
    }

    const labels = monthBuckets.map((d) => formatMonth(d));
    const counts = monthBuckets.map((bucketStart) => {
      const y = bucketStart.getFullYear();
      const m = bucketStart.getMonth();
      return bookings.filter((b) => {
        const created = new Date(b.createdAt);
        return created.getFullYear() === y && created.getMonth() === m;
      }).length;
    });

    return {
      labels,
      datasets: [
        {
          label: "Bookings",
          data: counts,
          borderColor: "#ff6b6b",
          backgroundColor: "rgba(255,107,107,0.15)",
          tension: 0.4,
          fill: true,
        },
      ],
    };
  }, [bookings]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
  };

  const downloadReport = () => {
    const generatedAt = new Date();
    const reportLines = [
      "BookMyRepair - Dashboard Report",
      `Generated: ${generatedAt.toLocaleString()}`,
      "",
      "Summary",
      `Total Brands: ${brands.length}`,
      `Total Models: ${stats.totalModels}`,
      `Total Orders: ${stats.totalOrders}`,
      `Total Customers: ${stats.totalCustomers}`,
      `Pending Repairs: ${stats.pendingRepairs}`,
      `Completed Repairs: ${stats.completedRepairs}`,
      "",
      "Recent Bookings",
      ...bookings.slice(0, 20).map((booking, index) => {
        const customer = booking.name || booking.customerName || "Unknown";
        const phone = booking.phone || "N/A";
        const brand = booking.brandName || booking.brand || "N/A";
        const model = booking.modelName || booking.model || "N/A";
        const status = booking.status || "Pending";
        return `${index + 1}. ${customer} | ${phone} | ${brand} ${model} | ${status}`;
      }),
    ];

    const blob = createSimplePdf(reportLines);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dashboard-report.pdf";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard Overview</h1>
          <p>
            {loading
              ? "Loading latest admin data..."
              : `Live data: ${brands.length} brands, ${stats.totalModels} models, ${stats.totalOrders} bookings.`}
          </p>
        </div>
        <button className="btn-primary" onClick={downloadReport}>
          Download Report
        </button>
      </div>

      <div className="dashboard-stats-grid">
        <div className="dashboard-stat-card card1">
          <h4>Total Orders</h4>
          <h2 style={{ color:"black" }}>{stats.totalOrders}</h2>
          <span>{stats.completedRepairs} completed</span>
        </div>

        <div className="dashboard-stat-card card2">
          <h4>Total Customers</h4>
          <h2>{stats.totalCustomers}</h2>
          <span>Unique phone numbers</span>
        </div>

        <div className="dashboard-stat-card card3">
          <h4>Total Models</h4>
          <h2>{stats.totalModels}</h2>
          <span>{brands.length} brands configured</span>
        </div>

        <div className="dashboard-stat-card card4">
          <h4>Pending Repairs</h4>
          <h2>{stats.pendingRepairs}</h2>
          <span>{stats.totalOrders === 0 ? "No bookings yet" : "Needs action"}</span>
        </div>
      </div>

      <div className="chart-card">
        <div className="chart-header">
          <h3>Booking Analytics</h3>
          <select disabled>
            <option>Last  Months</option>
          </select>
        </div>

        <div className="chart-wrapper">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
